import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Star,
  Calendar,
  Clock,
  Ticket,
  Info,
  Heart,
  Share2,
} from "lucide-react";

const HeroCarousel = () => {
  const [movies, setMovies] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const carouselRef = useRef(null);

  // Fetch movies from backend
  useEffect(() => {
    axios
      .get("https://apigrand.dodunsoftsolutions.com/api/movie/all")
      .then((res) => {
        const filtered = res.data.data.filter(
          (movie) =>
            movie.status?.toLowerCase() === "nowshowing" ||
            movie.status?.toLowerCase() === "upcoming"
        );
        setMovies(filtered);
      })
      .catch((err) => console.error("API Error:", err));
  }, []);

  const goToSlide = useCallback(
    (idx) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentIndex(idx);
      setTimeout(() => setIsTransitioning(false), 700); // Increased transition time
    },
    [isTransitioning]
  );

  const goToPrevSlide = useCallback(() => {
    goToSlide((currentIndex - 1 + movies.length) % movies.length);
  }, [currentIndex, movies.length, goToSlide]);

  const goToNextSlide = useCallback(() => {
    goToSlide((currentIndex + 1) % movies.length);
  }, [currentIndex, movies.length, goToSlide]);

  useEffect(() => {
    if (!autoplay || isHovered) return;
    const id = setInterval(goToNextSlide, 3000);

    return () => clearInterval(id);
  }, [autoplay, isHovered, goToNextSlide]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goToPrevSlide();
      if (e.key === "ArrowRight") goToNextSlide();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goToPrevSlide, goToNextSlide]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setAutoplay(false);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setAutoplay(true);
  };

  const renderStars = (rating) => {
    const stars = [];
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;

    for (let i = 0; i < full; i++) {
      stars.push(
        <Star key={i} size={18} className="fill-amber-400 text-amber-400" />
      );
    }

    if (half) {
      stars.push(
        <div key="half" className="relative">
          <Star size={18} className="text-amber-400" />
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star size={18} className="fill-amber-400 text-amber-400" />
          </div>
        </div>
      );
    }

    while (stars.length < 5) {
      stars.push(
        <Star key={`e${stars.length}`} size={18} className="text-gray-400" />
      );
    }

    return <div className="flex">{stars}</div>;
  };

  if (!movies.length) {
    return (
      <div className="h-[700px] flex items-center justify-center bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-16 h-16">
            <div className="absolute top-0 left-0 w-full h-full border-4 border-t-red-600 border-r-amber-500 border-b-blue-600 border-l-green-500 rounded-full animate-spin"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-black rounded-full"></div>
          </div>
          <p className="text-white text-xl font-medium">
            Loading amazing movies...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={carouselRef}
      className="relative w-full h-[650px] md:h-[750px] overflow-hidden bg-gray-900"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Slides */}
      <div className="h-full">
        {movies.map((movie, idx) => {
          const isActive = idx === currentIndex;
          return (
            <div
              key={movie._id}
              className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                isActive
                  ? "opacity-100 z-10 scale-100"
                  : "opacity-0 z-0 scale-110"
              }`}
            >
              <div className="relative w-full h-full">
                <img
                  src={movie.imagebanner || movie.image}
                  alt={movie.movieName}
                  className={`w-full h-full object-cover ${
                    isActive ? "animate-kenburns" : ""
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />

                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />

                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.5)_70%)]" />
              </div>

              <div
                className={`absolute bottom-16 md:bottom-24 left-0 w-full md:w-3/5 p-6 md:p-16 text-white z-20 ${
                  isActive ? "animate-slide-up" : ""
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  {movie.comingSoon ? (
                    <div className="inline-flex items-center bg-gradient-to-r from-amber-500 to-yellow-400 text-red-900 font-medium px-5 py-2 rounded-full text-sm animate-pulse-custom shadow-lg shadow-amber-500/30">
                      <Calendar className="w-4 h-4 mr-2" />
                      Coming Soon
                    </div>
                  ) : (
                    <div className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-500 text-white font-medium px-5 py-2 rounded-full text-sm shadow-lg shadow-red-600/30">
                      <Ticket className="w-4 h-4 mr-2" />
                      Now Showing
                    </div>
                  )}

                  <div className="bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full flex items-center">
                    <span className="font-semibold">
                      {movie.genre?.join(", ") || "Action, Drama"}
                    </span>
                  </div>
                </div>

                <h1
                  className={`text-3xl md:text-7xl font-bold drop-shadow-lg mb-6 ${
                    isActive ? "animate-fade-in" : ""
                  }`}
                >
                  {movie.movieName}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-5">
                  <span className="text-amber-400 font-medium text-lg">
                    {movie.language}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  <div className="flex items-center gap-1">
                    {renderStars(parseFloat(movie.imdbRating))}
                    <span className="ml-2 text-gray-300 font-medium">
                      ({movie.imdbRating || "--"})
                    </span>
                  </div>
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  <div className="flex items-center text-gray-300">
                    <Clock size={18} className="mr-2 text-red-500" />
                    <span className="font-medium">{movie.duration} min</span>
                  </div>
                </div>

                <div className="flex items-center text-gray-300 mb-5">
                  <Calendar size={18} className="mr-2 text-amber-400" />
                  <span className="font-medium">
                    {new Date(movie.releaseDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <p className="text-gray-300 text-md leading-relaxed max-w-2xl mb-8 backdrop-blur-sm bg-black/20 p-4 rounded-xl border border-white/10">
                  {movie.description?.length > 200
                    ? movie.description.slice(0, 200) + "..."
                    : movie.description}
                </p>

                <div
                  className={`flex flex-wrap gap-5 ${
                    isActive ? "animate-fade-in-delay" : ""
                  }`}
                >
                  <a
                    href={movie.trailerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-amber-500 to-yellow-400 text-red-900 font-bold px-8 py-4 rounded-full flex items-center hover:scale-105 hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300"
                  >
                    <Play className="mr-2 fill-red-900" />
                    Watch Trailer
                  </a>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute top-12 right-12 animate-float opacity-70 w-64 h-64 bg-gradient-to-br from-red-500/10 to-amber-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-36 right-36 animate-float-delay opacity-60 w-96 h-96 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <div className="absolute inset-0 flex justify-between items-center px-6 md:px-16">
        <button
          onClick={goToPrevSlide}
          disabled={isTransitioning}
          className="bg-black/20 backdrop-blur-sm text-white p-3 md:p-5 rounded-full hover:bg-red-900/50 hover:scale-110 transition-all duration-300 group border border-white/10"
        >
          <ChevronLeft size={28} className="group-hover:animate-pulse-x" />
        </button>
        <button
          onClick={goToNextSlide}
          disabled={isTransitioning}
          className="bg-black/20 backdrop-blur-sm text-white p-3 md:p-5 rounded-full hover:bg-red-900/50 hover:scale-110 transition-all duration-300 group border border-white/10"
        >
          <ChevronRight size={28} className="group-hover:animate-pulse-x" />
        </button>
      </div>

      {/* Progress dots */}
      <div className="absolute bottom-6 w-full flex justify-center gap-3">
        {movies.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`transition-all duration-700 rounded-full ${
              idx === currentIndex
                ? "w-12 h-2 bg-gradient-to-r from-amber-500 to-red-600 shadow-glow"
                : "w-2 h-2 bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Custom keyframes and styles */}
      <style jsx>{`
        @keyframes pulse-custom {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(0.98);
          }
        }
        .animate-pulse-custom {
          animation: pulse-custom 2s infinite;
        }

        @keyframes kenburns {
          0% {
            transform: scale(1.05) translate(-0.5%, -0.5%);
          }
          100% {
            transform: scale(1) translate(0, 0);
          }
        }
        .animate-kenburns {
          animation: kenburns 8s ease-out forwards;
        }

        @keyframes slide-up {
          0% {
            transform: translateY(30px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 1s ease-out forwards;
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 1.2s ease-out forwards;
        }

        @keyframes fade-in-delay {
          0%,
          30% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .animate-fade-in-delay {
          animation: fade-in-delay 1.8s ease-out forwards;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-15px) scale(1.05);
          }
        }
        .animate-float {
          animation: float 15s ease-in-out infinite;
        }

        @keyframes float-delay {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(15px) scale(1.05);
          }
        }
        .animate-float-delay {
          animation: float-delay 18s ease-in-out infinite;
        }

        @keyframes pulse-x {
          0%,
          100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(-3px);
          }
        }
        .animate-pulse-x {
          animation: pulse-x 0.5s ease-in-out infinite;
        }

        .shadow-glow {
          box-shadow: 0 0 8px 2px rgba(239, 68, 68, 0.3);
        }
      `}</style>
    </div>
  );
};

export default HeroCarousel;
